import {
  type CollectionQuerySpec,
  PARAM_LIMITS,
  type Params,
  type ParamValue,
  PLACEHOLDER,
  type QuerySpec,
} from "./manifest";
import type { Json, Rec } from "./records";

/**
 * The page API's queries (docs/views.md#6-the-page-api): a where clause matches a value, `{ in }`, `{ has }` for lists, `{ not }`,
 * `{ gt }`, or `{ lt }`; `search` looks through a record's text; sort takes field names, `-` first for descending.
 */
export type Condition = Json | { in: Json[] } | { has: Json } | { not: Json } | { gt: Json } | { lt: Json };
export interface Query {
  where?: Record<string, Condition>;
  search?: string;
  sort?: string | string[];
  limit?: number;
  chat?: string;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const OPERATORS = ["in", "has", "not", "gt", "lt"];
const isCondition = (value: unknown): value is Record<string, Json> =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0 &&
  Object.keys(value).every((key) => OPERATORS.includes(key));

export function compare(a: unknown, b: unknown): number {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** Whether a record matches a query's where clause and search. `chat` is the record's chat, when it has one. */
export function matches(rec: Rec & { chat?: string | null }, query: Query = {}): boolean {
  if (query.chat && rec.chat !== query.chat) return false;
  for (const [field, test] of Object.entries(query.where || {})) {
    const value = rec[field];
    const has = (x: unknown) => (Array.isArray(value) ? value.some((v) => same(v, x)) : same(value, x));
    if (isCondition(test)) {
      if ("in" in test && !(Array.isArray(test.in) && test.in.some((x) => has(x)))) return false;
      if ("has" in test && !(Array.isArray(value) && value.some((v) => same(v, test.has)))) return false;
      if ("not" in test && has(test.not)) return false;
      // A record without the field is neither more nor less than anything (docs/spec/view-format.md#3-data).
      if ("gt" in test && !(value != null && compare(value, test.gt) > 0)) return false;
      if ("lt" in test && !(value != null && compare(value, test.lt) < 0)) return false;
    } else if (!has(test)) return false;
  }
  if (query.search) {
    const needle = query.search.toLowerCase();
    const text = Object.entries(rec)
      .filter(([key]) => !["id", "n", "chat", "createdAt", "createdBy", "updatedAt", "updatedBy"].includes(key))
      .map(([, value]) =>
        Array.isArray(value)
          ? value.join(" ")
          : typeof value === "string" || typeof value === "number"
            ? String(value)
            : "",
      )
      .join(" ")
      .toLowerCase();
    if (!text.includes(needle)) return false;
  }
  return true;
}

/** A where clause with `"me"` read as `viewer`: as a value, and as the value or an item of a condition. */
function asViewer(where: Query["where"], viewer: string): Query["where"] {
  const me = (value: Json): Json => (value === "me" ? viewer : Array.isArray(value) ? value.map(me) : value);
  return Object.fromEntries(
    Object.entries(where ?? {}).map(([field, test]) => [
      field,
      isCondition(test)
        ? Object.fromEntries(Object.entries(test).map(([op, value]) => [op, me(value)]))
        : me(test as Json),
    ]),
  );
}

/** A query run over records: filtered, sorted, and cut to its limit. `"me"` in its where clause is `viewer`, when given. */
export function run<T extends Rec>(list: T[], query: Query = {}, viewer?: string | null): T[] {
  if (viewer && query.where) query = { ...query, where: asViewer(query.where, viewer) };
  let out = list.filter((rec) => matches(rec, query));
  const keys = ([] as string[]).concat(query.sort || []);
  if (keys.length)
    out.sort((a, b) => {
      for (const key of keys) {
        const desc = key.startsWith("-"),
          field = desc ? key.slice(1) : key;
        const x = a[field],
          y = b[field];
        // Records without the field come last, ascending or descending.
        if (x == null || y == null) {
          if (x == null && y == null) continue;
          return x == null ? 1 : -1;
        }
        const c = compare(x, y);
        if (c) return desc ? -c : c;
      }
      return 0;
    });
  if (query.limit && query.limit > 0) out = out.slice(0, query.limit);
  return out;
}

/**
 * A declared query's or action's parameters as a call gives them (docs/views.md#14-queries-and-actions): each one the
 * manifest names, of its type and up to 200 characters, or as many as `chars` gives it, a `strings` list of up to ten of
 * them, over the manifest's defaults, under its fixed values; or why not.
 */
export function queryValues(
  spec: Params,
  name: string,
  params: Record<string, unknown>,
  chars: Readonly<Record<string, number>> = {},
): { values: Record<string, ParamValue | string[]> } | { error: string } {
  const values: Record<string, ParamValue | string[]> = { ...(spec.defaults ?? {}) };
  for (const [param, value] of Object.entries(params)) {
    const type = spec.params[param];
    if (!type)
      return {
        error:
          spec.fixed && param in spec.fixed
            ? `${name} always sends ${param} as the manifest fixes it.`
            : `${name} takes no parameter ${param}.`,
      };
    if (value === undefined || value === null) continue;
    if (type === "strings") {
      if (
        !Array.isArray(value) ||
        value.length > PARAM_LIMITS.items ||
        value.some((item) => typeof item !== "string" || item.length > PARAM_LIMITS.chars)
      )
        return {
          error: `${param} is a list of up to ${PARAM_LIMITS.items} strings of up to ${PARAM_LIMITS.chars} characters.`,
        };
      values[param] = value as string[];
      continue;
    }
    const most = chars[param] ?? PARAM_LIMITS.chars;
    if (typeof value !== type || (type === "number" && !Number.isFinite(value)) || String(value).length > most)
      return { error: `${param} is a ${type} of up to ${most} characters.` };
    values[param] = value as ParamValue;
  }
  return { values: { ...values, ...(spec.fixed ?? {}) } };
}

/**
 * A declared collection query with its parameters filled in: its where clause, with each `"{name}"` the parameter's
 * value, and a condition whose parameter the call left out dropped, so a parameter filters only when it is given; and
 * the query's sort and limit. `"me"` stays for `run` to read as the viewer.
 */
export function collectionQuery(spec: CollectionQuerySpec, values: Record<string, ParamValue | string[]>): Query {
  const where: Record<string, Condition> = {};
  const fill = (value: unknown): { value: unknown } | null => {
    if (typeof value === "string") {
      const param = PLACEHOLDER.exec(value)?.[1];
      if (!param) return { value };
      return param in values ? { value: values[param] } : null;
    }
    if (Array.isArray(value)) {
      const items = value.map(fill);
      return items.every(Boolean) ? { value: items.map((item) => item!.value) } : null;
    }
    if (value && typeof value === "object") {
      const entries = Object.entries(value).map(([key, item]) => [key, fill(item)] as const);
      return entries.every(([, item]) => item)
        ? { value: Object.fromEntries(entries.map(([key, item]) => [key, item!.value])) }
        : null;
    }
    return { value };
  };
  for (const [field, test] of Object.entries(spec.where ?? {})) {
    const filled = fill(test);
    if (filled) where[field] = filled.value as Condition;
  }
  return {
    ...(Object.keys(where).length ? { where } : {}),
    ...(spec.sort ? { sort: spec.sort } : {}),
    ...(spec.limit ? { limit: spec.limit } : {}),
  };
}

/**
 * One of a view's queries answered where its records are: a collection query over what `find` reads, as `viewer`;
 * null for a query this can't answer, one through a connector, which the server runs. The device's copy and the
 * server's rows answer with this same function, so the page, a posted view, and an agent get the same answer.
 */
export function answerLocally(
  queries: Record<string, QuerySpec>,
  name: string,
  params: Record<string, unknown>,
  viewer: string | null,
  find: (collection: string, query: Query, viewer: string | null) => Rec[],
): { value: Rec[] } | { error: string } | null {
  const spec = queries[name];
  if (!spec) return { error: `This view has no query named ${name}.` };
  if (!("collection" in spec)) return null;
  const checked = queryValues(spec, name, params);
  if ("error" in checked) return checked;
  return { value: find(spec.collection, collectionQuery(spec, checked.values), viewer) };
}
